import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProspectMembers } from '../../../hooks/useApi';
import Pagination from '../../../components/Pagination';
import Swal from 'sweetalert2';
import ApiService from '../../../services/api';

const ProspectiveMemberManagement = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // Memoize API parameters to prevent unnecessary re-renders
  const membersParams = useMemo(() => ({
    search: searchTerm,
    status: statusFilter,
    page: currentPage,
    limit: 10,
    paginate: true
  }), [searchTerm, statusFilter, currentPage]);

  const { data: membersData, loading, error, refetch } = useProspectMembers(membersParams);

  const members = membersData?.data || [];
  const pagination = membersData?.pagination || {};

  // Handle status change actions
  const handleStatusChange = async (memberId, action) => {
    setIsLoading(true);
    try {
      let response;
      switch (action) {
        case 'approve':
          response = await ApiService.patch(`/members/${memberId}/approve`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member approved successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        case 'reject':
          response = await ApiService.patch(`/members/${memberId}/reject`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member rejected successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        case 'suspend':
          response = await ApiService.patch(`/members/${memberId}/suspend`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member suspended successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        default:
          throw new Error('Invalid action');
      }
      
      // Refetch data to update the UI
      refetch();
    } catch (error) {
      console.error(`Error ${action}ing member:`, error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || `Failed to ${action} member`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      active: 'bg-green-100 text-green-800 border-green-300',
      inactive: 'bg-red-100 text-red-800 border-red-300',
      suspended: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading members</div>
          <button 
            onClick={refetch}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Prospective Member Management</h1>
          <p className="text-gray-600">Manage and approve cooperative member registrations</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/members/create"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Member
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search Members
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, phone, member ID, or occupation..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading members...</span>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">No members found</div>
            <p className="text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No members have registered yet.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{member.full_name}</div>
                          <div className="text-sm text-gray-500">ID: {member.member_id_number}</div>
                          <div className="text-sm text-gray-500">{member.occupation}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{member.phone_number}</div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadge(member.status)}`}>
                          {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* View Details */}
                          <Link
                            to={`/members/${member.id}`}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            View
                          </Link>

                          {/* Status Actions */}
                          {member.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleStatusChange(member.id, 'approve')}
                                disabled={isLoading}
                                className="text-green-600 hover:text-green-900 font-medium disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleStatusChange(member.id, 'reject')}
                                disabled={isLoading}
                                className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}

                          {member.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(member.id, 'suspend')}
                              disabled={isLoading}
                              className="text-orange-600 hover:text-orange-900 font-medium disabled:opacity-50"
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.per_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProspectiveMemberManagement;
